import React, { useState, useEffect } from "react";
import Select from "react-select";

import { timestampToDateString, toOptionArray } from "../../util";

const customStyles = {
  control: (base) => ({
    ...base,
    minHeight: "3rem",
    backgroundColor: "var(--fallback-b1, oklch(var(--b1) / var(--tw-bg-opacity)))",
    borderRadius: "var(--rounded-btn, 0.5rem)",
    borderWidth: "1px",
    borderColor: "transparent",
    outlineColor: "var(--fallback-bc, oklch(var(--bc) / 0.2))",
    outlineOffset: "2px",
    boxShadow: "none",
    "&:hover": {
      borderColor: "transparent",
      cursor: "pointer",
    },
  }),
};

export const Options = (props) => {
  let [data, setData] = useState(props.searchParams);

  // Sync local state when search params change (e.g., when cleared)
  useEffect(() => {
    setData(props.searchParams);
  }, [props.searchParams]);

  const displayOptions = [
    { value: "chart", label: "Chart" },
    { value: "table", label: "Table" },
  ];

  const groupByOptions = [
    { value: "day", label: "Day" },
    { value: "week", label: "Week" },
    { value: "month", label: "Month" },
  ];

  return (
    <div className="drawer-side max-h-[90dvh]">
      <label
        htmlFor="chart-options-drawer"
        aria-label="close sidebar"
        className="drawer-overlay"
      ></label>
      <ul className="list p-4 w-80 min-h-full bg-base-200 text-base-content text-left">
        <li className="list-row py-2">
          <span className="block text-sm font-medium">Display type</span>
          <Select
            key={`display-${data.toString()}`}
            value={displayOptions.find((e) => e.value === data.get("type"))}
            styles={customStyles}
            options={displayOptions}
            onChange={(e) => {
              setData((previous) => {
                let newVal = new URLSearchParams(previous);
                if (e.value === "chart") {
                  props.setShowChart(true);
                } else if (e.value === "table") {
                  props.setShowChart(false);
                }
                newVal.set("type", e.value);
                return newVal;
              });
            }}
          ></Select>
        </li>
        <li className="list-row py-2">
          <span className="block text-sm font-medium">Company</span>
          <Select
            key={`companies-${data.toString()}`}
            options={toOptionArray(props.companies, "Name")}
            styles={customStyles}
            isMulti
            onChange={(e) => {
              setData((previous) => {
                let newVal = new URLSearchParams(previous);
                newVal.delete("companies");
                e.forEach((c) => newVal.append("companies", c.value));
                let sites = data.getAll("sites");
                let availableIDs = props.sites
                  ?.filter((site) => {
                    return newVal
                      .getAll("companies")
                      .includes(site.data().CompanyID);
                  })
                  .map((site) => site.id);
                sites
                  .filter((v) => !availableIDs.includes(v))
                  .forEach((v) => newVal.delete("sites", v));
                return newVal;
              });
            }}
            value={toOptionArray(
              props.companies?.filter((c) =>
                data.getAll("companies").includes(c.id)
              ),
              "Name"
            )}
          />
        </li>
        <li className="list-row py-2">
          <span className="block text-sm font-medium">Sites</span>
          <Select
            key={`sites-${data.toString()}`}
            isMulti
            styles={customStyles}
            options={
              data.getAll("companies").length
                ? toOptionArray(
                    props.sites?.filter((site) => {
                      return data
                        .getAll("companies")
                        .includes(site.data().CompanyID);
                    }),
                    "Name"
                  )
                : toOptionArray(props.sites, "Name")
            }
            onChange={(e) => {
              setData((previous) => {
                let newVal = new URLSearchParams(previous);
                newVal.delete("sites");
                e.map((s) => newVal.append("sites", s.value));
                return newVal;
              });
            }}
            value={toOptionArray(
              props.sites?.filter((site) =>
                data.getAll("sites").includes(site.id)
              ),
              "Name"
            )}
          />
        </li>
        {props.showChart && (
          <li className="list-row py-2">
            <span className="block text-sm font-medium">Variable 1</span>
            <Select
              key={`var1-${data.toString()}`}
              isClearable
              styles={customStyles}
              value={toOptionArray(props.variables, "ColumnName").find(
                (e) => e.value === data.get("var1")
              )}
              options={toOptionArray(props.variables, "ColumnName")}
              onChange={(e) => {
                setData((previous) => {
                  let newVal = new URLSearchParams(previous);
                  newVal.delete("var1");
                  if (e && e.value) newVal.set("var1", e.value);
                  return newVal;
                });
              }}
            ></Select>
          </li>
        )}
        {props.showChart && (
          <li className="list-row py-2">
            <span className="block text-sm font-medium">Variable 2</span>
            <Select
              key={`var2-${data.toString()}`}
              isClearable
              value={toOptionArray(props.variables, "ColumnName").find(
                (e) => e.value === data.get("var2")
              )}
              styles={customStyles}
              options={toOptionArray(props.variables, "ColumnName")}
              onChange={(e) => {
                setData((previous) => {
                  let newVal = new URLSearchParams(previous);
                  newVal.delete("var2");
                  if (e && e.value) newVal.set("var2", e.value);
                  return newVal;
                });
              }}
            ></Select>
          </li>
        )}
        {props.showChart && (
          <li className="list-row py-2">
            <span className="block text-sm font-medium">Group by</span>
            <Select
              key={`groupBy-${data.toString()}`}
              isClearable
              value={groupByOptions.find(
                (o) => o.value == data.get("groupBy")
              )}
              styles={customStyles}
              options={groupByOptions}
              onChange={(e) => {
                setData((previous) => {
                  let newVal = new URLSearchParams(previous);
                  newVal.delete("groupBy");
                  if (e && e.value) newVal.set("groupBy", e.value);
                  return newVal;
                });
              }}
            ></Select>
          </li>
        )}
        <li className="list-row py-2">
          <span className="block text-sm font-medium">Start date:</span>
          <input
            key={`startDate-${data.get("min")}`}
            type="date"
            className="input w-full max-w-xs"
            value={timestampToDateString(data.get("min")) || ""}
            onChange={(e) => {
              setData((previous) => {
                let newVal = new URLSearchParams(previous);
                if (!e.target.value) {
                  newVal.delete("min");
                } else {
                  newVal.set("min", new Date(e.target.value).getTime());
                }
                return newVal;
              });
            }}
          />
        </li>
        <li className="list-row py-2">
          <span className="block text-sm font-medium">End date:</span>
          <input
            key={`endDate-${data.get("max")}`}
            type="date"
            className="input w-full max-w-xs"
            value={timestampToDateString(data.get("max")) || ""}
            onChange={(e) => {
              setData((previous) => {
                let newVal = new URLSearchParams(previous);
                if (!e.target.value) {
                  newVal.delete("max");
                } else {
                  newVal.set("max", new Date(e.target.value).getTime());
                }
                return newVal;
              });
            }}
          />
        </li>
        <li className="list-row pt-7">
          <button
            type="button"
            className="btn btn-primary w-full"
            onClick={() => props.setSearchParams(data)}
          >
            Apply
          </button>
        </li>
        <li className="list-row pt-2">
          <button
            type="button"
            className="btn btn-outline btn-sm w-full"
            onClick={props.onClear}
          >
            Clear All
          </button>
        </li>
      </ul>
    </div>
  );
};
